import { isApprovalResolutionCode } from '@clockwise/shared';

/**
 * Render `approval_instances.resolution_reason` for a human.
 *
 * That column carries two different kinds of content and the UI used to
 * print both the same way:
 *
 *   - a REJECTION reason, free text an approver typed — show verbatim,
 *     it is the whole point of opening the row;
 *   - a SYSTEM outcome on a failed instance, which nobody wrote. It used
 *     to be operator prose ("No eligible approvers for step 1 (1 routed,
 *     none eligible to decide)") shown to employees, who have no idea
 *     what a routed step is. It is now a stable code (see
 *     `APPROVAL_RESOLUTION_CODES`) that this maps to plain copy.
 *
 * Anything that is not a known code passes through unchanged. That is
 * both the legacy path — rows written before codes shipped still hold the
 * old prose and there is no backfill — and the permanent path for human
 * rejection reasons.
 *
 * The copy says what happened and who fixes it, and deliberately does not
 * name who was dropped: that is org structure the submitter should not be
 * shown, and it lives in the server's `warn` log instead.
 */
const CODE_TEXT: Record<string, string> = {
  // Nobody was routed at all — the customer has no team lead, or the
  // policy points at an empty set. An admin fixes the team or the policy.
  NO_APPROVERS_ROUTED: 'No approver is set up for this work, so it could not be sent for approval.',
  // People were routed, but none of them may decide it — missing
  // permission, or they are outside their employment dates. An admin
  // fixes that person's access, or points the approval somewhere else.
  ROUTED_APPROVERS_INELIGIBLE:
    "The assigned approver isn't able to approve this, so it could not be sent for approval.",
};

/**
 * Who wrote the text, alongside the text itself.
 *
 * Readers of a rejection banner could not tell the approver's own words
 * from the app's — both rendered as the same plain sentence, so "auto
 * approved" (typed by a person) sat next to "…then resubmit for approval"
 * (written by us) with nothing to separate them. Callers use `kind` to
 * give the two different voices.
 *
 *   - `note`   — free text a human approver typed. Quote it and attribute it.
 *   - `system` — copy this app wrote for a machine outcome. No attribution.
 */
export type ApprovalResolution = { kind: 'note' | 'system'; text: string };

/**
 * The entry's STATUS decides who wrote the reason. Not the reason's text.
 *
 * `resolution_reason` is only ever set on `rejected` or `failed` (the
 * `timesheets_resolution_reason_matches_status` CHECK), and since the
 * server started mapping a decider-less rejection to `failed`, those two
 * statuses partition the space exactly:
 *
 *   - `rejected` — reachable ONLY from a rejection a person made
 *     (`outcomeTargetState` sends every decider-less one to `failed`), so
 *     the reason is that person's own words;
 *   - `failed`   — no policy matched, or no approver could be routed.
 *     Nobody read the entry; the reason is a code or the server's prose.
 *
 * This replaced a list of server-written phrases matched by value, which
 * was brittle in both directions: it silently mis-attributed any wording
 * the list hadn't learned, and it would have started lying the moment
 * someone reworded a message on the server. Deriving from the state
 * machine can't drift, because the same mapping that sets the status is
 * the one that decides what the reason means.
 *
 * KNOWN GAP: rows written before that mapping shipped. A no-match entry
 * from back then still sits at `rejected` carrying 'No matching approval
 * policy', and reads here as an approver's note. The fix is the backfill
 * (see the PR description), not another list of strings — the editor
 * additionally corrects those from the decider lookup.
 */
export function approvalResolution(
  reason: string | null | undefined,
  status: string,
): ApprovalResolution | null {
  if (!reason) return null;
  const text = isApprovalResolutionCode(reason) ? (CODE_TEXT[reason] ?? reason) : reason;
  return { kind: status === 'rejected' ? 'note' : 'system', text };
}

/** Just the display text — for tooltips and other places that show the
 *  reason without distinguishing voices. */
export function approvalResolutionText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return isApprovalResolutionCode(reason) ? (CODE_TEXT[reason] ?? reason) : reason;
}
